import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UbiAuthorizer, UbiAuthFactory, UbiAuthISOCognitoUsernamePasswordService } from 'uber-ui';

import { ApiAbstractService } from './api-abstract.service';
import { Role } from 'modules/shared/models/role.models';
import { CurrentUser } from 'modules/shared/models/account.models';
import { LoginResponse, ForgotPasswordResponse, ICredentials } from 'modules/shared/models/auth.models';

import { environment } from 'environments/environment';

@Injectable()
export class AuthService extends ApiAbstractService {

  private cognitoUser: any;

  authorizer: UbiAuthorizer;

  constructor(private ubiAuthFactory: UbiAuthFactory,
    private httpClient: HttpClient
  ) {
    super(environment.API_BASE);
    this.authorizer = this.ubiAuthFactory.create(UbiAuthISOCognitoUsernamePasswordService);
    this.authorizer.configurations({
      userPoolId: environment.POOL_ID,
      clientId: environment.CLIENT_ID,
      endpoint: environment['AUTHORIZER_ENDPOINT'] ? environment['AUTHORIZER_ENDPOINT'] : ''
    });
  }

  /**
   * Do login
   * @method login
   */
  login(credentials: ICredentials): Observable<LoginResponse> {
    return this.authorizer.login(credentials)
      .pipe(
        map((response: LoginResponse) => {
          if (response.challenge) {
            return {
              user: response.user,
              challenge: response.challenge
            };
          }
          return {
            user: {
              username: response.user['cognito:username'],
              lastChangePassword: response.user['custom:last_change_password'],
              lastLogin: response.user['custom:last_login'],
              email: response.user.email,
              sub: response.user.sub,
              name: response.user.name
            } as CurrentUser,
            token: response.token,
            accessToken: response.accessToken
          } as LoginResponse;
        })
      );
  }

  /**
   * Do logout
   * @method logOut
   */
  logOut(): Observable<boolean> {
    return this.authorizer.logout()
      .pipe(
        map(() => true),
        catchError(() => of(true)) // do logout even failed
      );
  }

  /**
   * Confirm mail or username when forgot password
   * @method forgotPassword
   */
  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    return this.authorizer.forgotPassword(email);
  }
  /**
   * Reset password
   * @method resetPassword
   */
  resetPassword(username: string, code: string, newPassword: string): Observable<boolean> {
    return this.authorizer.confirmForgotPassword(username, code, newPassword)
      .pipe(
        map(() => true)
      );
  }

  /**
   * Change password
   * @method changePassword
   */
  changePassword(oldPassword: string, newPassword: string): Observable<boolean> {
    return this.authorizer.changePassword(this.cognitoUser, oldPassword, newPassword)
      .pipe(
        map(() => true)
      );
  }

  /**
   * Get current user
   * @method getUser
   */
  getUser(bypassCache: boolean = false): Observable<CurrentUser> {
    return this.authorizer.getUser(bypassCache).pipe(
      map((user: any) => {
        this.cognitoUser = user;
        return {
            username: user.username,
            lastChangePassword: user.attributes['custom:last_change_password'],
            lastLogin: user.attributes['custom:last_login'],
            email: user.attributes.email,
            sub: user.attributes.sub,
            name: user.attributes.name,
            preferredMFA: user.preferredMFA,
            signInUserSession: user.signInUserSession
          } as CurrentUser;
      }),
      catchError(() => throwError('NotAuthenticated'))
    );
  }

  /**
   * Get current session
   * @method getSession
   */
  getSession(): Observable<any> {
    return this.authorizer.getSession()
      .pipe(
        map(session => session),
        catchError(() => throwError('NotAuthenticated'))
      );
  }

  /**
   * Reset the password for first login
   * @method completeFirstLogin
   */
  completeFirstLogin(user: any, newPassword: string): Observable<any> {
    return this.authorizer.completeNewPassword(user, newPassword);
  }

  /**
   * Confirm ninety days password
   * @method confirmNinetyDaysChangePassword
   */
  confirmNinetyDaysChangePassword(user: any): Observable<boolean> {
    return this.authorizer.confirmNinetyDaysChangePassword(user)
      .pipe(
        map(() => true)
      );
  }

  /**
   * Update user name
   * @method updateUserName
   */
  updateUserName(userName: string): Observable<boolean> {
    return this.authorizer.updateAttributes(this.cognitoUser, { name: userName })
      .pipe(
        map((response: string) => {
          return response === 'SUCCESS';
        })
      );
  }

  /**
   * Update user name to DB
   * @method updateUserNameDB
   */
  updateUserNameDB(oauthID: string, name: string): Observable<any> {
    return this.httpClient.put<any>(`${environment.API_BASE}/api/users/${oauthID}/`, { id: oauthID, name});
  }

  /**
   * Links username together with token in db
   * @method synchronizeUserName
   */
  synchronizeUserName(sub: string, name: string): Observable<any> {
    return this.httpClient.patch<any>(this.buildUrl(`/api/users/${sub}/`), { name : name});
  }

  /**
   * Enable MFA
   * @method enableMFA
   */
  enableMFA(): Observable<any> {
    return this.authorizer.enableMfa(this.cognitoUser);
  }

  /**
   * Confirm to enable MFA
   * @method enableMFAConfirm
   */
  enableMFAConfirm(code: string): Observable<boolean> {
    return this.authorizer.enableMfaConfirm(this.cognitoUser, code)
      .pipe(
        map(() => true)
      );
  }

  /**
   * Disable MFA
   * @method disableMFA
   */
  disableMFA(): Observable<any> {
    return this.authorizer.disableMfa(this.cognitoUser)
      .pipe(
        map(() => true)
      );
  }

  /**
   * Confirm MFA to login
   * @method twoFAConfirm
   */
  twoFAConfirm(username: string, code: string): Observable<any> {
    return this.authorizer.twoFAConfirm(username, code, 'SOFTWARE_TOKEN_MFA');
  }

  /**
   * Resend code to confirm
   * @method twoFAResend
   */
  twoFAResend(identifier: any): Observable<any> {
    return this.authorizer.twoFAResend(identifier);
  }

  /**
   * Get current access token
   * @method getToken
   */
  getToken(): Observable<any> {
    return this.authorizer.getToken();
  }

  /**
   * Get current auth state
   * @method getAuthState
   */
  getAuthState(): Observable<any> {
    return this.authorizer.authState();
  }

  /**
   * Get user roles by current domain
   * @method getRoles
   */
  getRoles(userId: string, domain: number): Observable<Array<Role>> {
    const url = `${environment.API_BASE}/api/users/${userId}/roles/`;
    return this.httpClient.get<Array<Role>>(url).pipe(
      map((res: Array<Role>) => res.filter(i => i.domain === domain))
    );
  }

  /**
   * Check DB existence
   * @method checkDbExistence
   */
  checkDbExistence(oauthId: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.API_BASE}/api/users/${oauthId}/`);
  }
}
